import { supabase, config } from './supabase';

// Extend window type for Lemon.js
declare global {
  interface Window {
    createLemonSqueezy?: () => void;
    LemonSqueezy?: {
      Url: {
        Open: (url: string) => void;
      };
    };
  }
}

/**
 * Service for handling Lemon Squeezy subscriptions
 */
export class SubscriptionService {
  /**
   * Create a checkout session for subscription
   * Returns the checkout URL
   */
  async createCheckout(userEmail: string, spaceId?: string): Promise<string> {
    const { storeId, productId } = config.lemonSqueezy;
    
    if (!storeId || !productId) {
      throw new Error('Lemon Squeezy configuration is missing');
    }

    try {
      // Get the current session to include auth header
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User must be authenticated to create checkout');
      }

      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          email: userEmail,
          storeId,
          productId,
          spaceId,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to create checkout');
      }

      if (!data?.success || !data?.checkoutUrl) {
        throw new Error(data?.error || 'No checkout URL returned');
      }

      return data.checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout:', error);
      throw error;
    }
  }

  /**
   * Initialize Lemon.js if not already loaded
   */
  private initLemonSqueezy(): void {
    if (!window.LemonSqueezy && window.createLemonSqueezy) {
      window.createLemonSqueezy();
    }
  }

  /**
   * Open subscription checkout as overlay using Lemon.js
   */
  async openCheckout(userEmail: string, spaceId?: string): Promise<void> {
    try {
      const checkoutUrl = await this.createCheckout(userEmail, spaceId);
      
      // Initialize Lemon.js
      this.initLemonSqueezy();
      
      // Open checkout as overlay
      if (window.LemonSqueezy?.Url?.Open) {
        window.LemonSqueezy.Url.Open(checkoutUrl);
      } else {
        // Fallback to opening in new window if Lemon.js is not available
        console.warn('Lemon.js not available, falling back to new window');
        window.open(checkoutUrl, '_blank');
      }
    } catch (error) {
      console.error('Error opening checkout:', error);
      throw error;
    }
  }

  /**
   * Handle webhook events from Lemon Squeezy
   * This should be called from a backend endpoint that receives webhooks
   */
  async handleWebhook(payload: any): Promise<void> {
    // Webhook payload contains subscription events
    // Events: subscription_created, subscription_updated, subscription_cancelled, etc.
    
    const eventName = payload.meta?.event_name;
    const customData = payload.data?.attributes?.custom_data;
    const subscriptionId = payload.data?.id;

    console.log('Received webhook:', eventName, customData, subscriptionId);

    // In a production app, you would:
    // 1. Verify webhook signature using SDK's webhook verification
    // 2. Update user's subscription status in Supabase
    // 3. Handle different event types (created, cancelled, expired, etc.)
    
    // Example implementation would call authService.updateUserTier()
    // with the appropriate tier based on subscription status
  }
}

export const subscriptionService = new SubscriptionService();
